import { Page } from "components/App/Page";
import { Aws } from "components/Integrations/Aws/Aws";
import { Azure } from "components/Integrations/Azure/Azure";
import { Gcloud } from "components/Integrations/Gcloud/Gcloud";
import { NEW_SENTINEL } from "components/Integrations/Install/Component";
import { once } from "lodash";
import { ReactElement, useCallback } from "react";
import { useParams } from "react-router-dom";

import { useNavigateWithSearchParams } from "../../Assessment/hooks/useNavigateWithSearchParams";
import { WelcomePaths, useWelcomePath } from "../paths";
import { StyledCloudContainer } from "../styles";
import { CloudProviderParams, SupportedCloudProviders } from "../types";

const cardOverride = {
  showBreadcrumbs: false,
};

const ProviderComponent: React.FC<{
  title: string;
  Component: React.ComponentType<any>;
  cardOverride: Record<string, any>;
  onInstall: (
    _config: Record<string, Record<string, { state: string }>>
  ) => void;
}> = ({ title, Component, cardOverride, onInstall }) => (
  <Page title={title}>
    <Component
      cardOverride={cardOverride}
      // The submit callback in Config.tsx runs 3 times for unknown reasons.
      // That causes the navigate to run 3 times, clogging the browser history.
      // Take a defensive approach here until that is resolved.
      onInstall={once(onInstall)}
    />
  </Page>
);

export const CloudProviderContainer: React.FC = () => {
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const params = useParams<CloudProviderParams>();
  const { cloudProvider, component } = params;

  const buildWelcomePath = useWelcomePath();

  const { orgSlug } = useParams<{
    orgSlug: string;
  }>();

  const navigateToIamWrite = useCallback(() => {
    navigateWithSearchParams(
      `/o/${orgSlug}/${WelcomePaths.Root}/${WelcomePaths.Onboarding}/${cloudProvider}/iam-write/${NEW_SENTINEL}`
    );
  }, [navigateWithSearchParams, orgSlug, cloudProvider]);

  const handleGenericInstall = useCallback(
    (_config: Record<string, Record<string, { state: string }>>) => {
      if (!cloudProvider || !component) return;
      // The case is the previous step, the navigate is the next step
      // e.g after AWS iam-write is installed, ask the user if they
      //  want to install resource inventory
      if (cloudProvider === "aws") {
        switch (component) {
          case "iam-write":
            navigateWithSearchParams(
              buildWelcomePath(WelcomePaths.InstallResource)
            );
            break;
          case "iam-assessment":
            navigateWithSearchParams(
              buildWelcomePath(WelcomePaths.ConfigureApprovers)
            );
            break;
          case "inventory":
            navigateWithSearchParams(
              buildWelcomePath(WelcomePaths.ConfigureApprovers)
            );
            break;
          default:
            navigateWithSearchParams(buildWelcomePath(WelcomePaths.Root));
            break;
        }
      } else if (cloudProvider === "azure") {
        switch (component) {
          case "iam-write":
            navigateWithSearchParams(
              buildWelcomePath(WelcomePaths.ConfigureApprovers)
            );
            break;
          case "azure-app":
            navigateToIamWrite();
            break;
          default:
            navigateWithSearchParams(buildWelcomePath(WelcomePaths.Root));
            break;
        }
      } else if (cloudProvider === "gcloud") {
        switch (component) {
          case "iam-write-security-perimeter":
            navigateToIamWrite();
            break;
          case "iam-write":
          case "iam-assessment":
            navigateWithSearchParams(
              buildWelcomePath(WelcomePaths.ConfigureApprovers)
            );
            break;
          default:
            navigateWithSearchParams(buildWelcomePath(WelcomePaths.Root));
            break;
        }
      }
    },
    [
      cloudProvider,
      component,
      navigateWithSearchParams,
      buildWelcomePath,
      navigateToIamWrite,
    ]
  );

  const providerCards: Record<SupportedCloudProviders, ReactElement> = {
    aws: (
      <ProviderComponent
        title={"AWS"}
        Component={Aws}
        cardOverride={cardOverride}
        onInstall={handleGenericInstall}
      />
    ),
    azure: (
      <ProviderComponent
        title={"Azure"}
        Component={Azure}
        cardOverride={cardOverride}
        onInstall={handleGenericInstall}
      />
    ),
    gcloud: (
      <ProviderComponent
        title={"Google Cloud"}
        Component={Gcloud}
        cardOverride={cardOverride}
        onInstall={handleGenericInstall}
      />
    ),
  };

  return (
    <StyledCloudContainer>
      {cloudProvider && providerCards[cloudProvider]}
    </StyledCloudContainer>
  );
};
